import {
  AutocompleteClasses,
  Autocomplete as BaseAutocomplete,
  CircularProgress,
  FormControl,
  InputBaseComponentProps,
  InputLabel,
  ListItem,
  MenuItem,
  Select,
  SxProps,
  TextField,
  Theme,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ReactNode } from "react-markdown";
import { useDebouncedValue } from "rooks";

import { AutocompleteContext } from "./contexts";
import { Listbox } from "./Listbox/Listbox";

export type ValidOptionValue = string | number | readonly string[] | undefined;

export type Option<Value extends ValidOptionValue> = {
  label: string;
  value: Value;
  bold?: boolean;
};

export type OptionsPage<Value extends ValidOptionValue> = {
  options: Option<Value>[];
  next?: string | null;
  previous?: string | null;
};

type AutocompleteProps<Value extends ValidOptionValue> = {
  value?: Option<Value> | null;
  loadOptions: (searchText: string, page?: OptionsPage<Value>) => Promise<OptionsPage<Value>>;
  onChange?: (option?: Option<Value>) => void;
  classes?: Partial<AutocompleteClasses>;
  popupIcon?: ReactNode;
  sx?: SxProps<Theme>;
  disabled?: boolean;
  inputProps?: InputBaseComponentProps;

  /*
   * If true, the first option is automatically highlighted.
   *
   * @default false
   */
  autoHighlight?: boolean;

  /**
   * @default Group
   */
  label?: string;

  /**
   * @default false
   */
  allOption?: Option<Value>;

  /**
   * @default false
   */
  createOption?: Option<Value>;

  /**
   * @default false
   */
  autoSelectFirst?: boolean;

  /**
   * @default false
   */
  defaultOption?: Option<Value>;

  /**
   * @default false
   */
  showLoadingIndicator?: boolean;
};

export function Autocomplete<Value extends ValidOptionValue>({
  value,
  onChange,
  allOption,
  classes,
  popupIcon,
  sx,
  autoSelectFirst = false,
  defaultOption,
  showLoadingIndicator = false,
  disabled,
  label = "Group",
  inputProps,
  autoHighlight = false,
  loadOptions,
  createOption,
}: AutocompleteProps<Value>) {
  const [searchText, setSearchText] = useState("");
  const searchTextRef = useRef(searchText);

  useEffect(() => {
    searchTextRef.current = searchText;
  }, [searchText]);

  const [debouncedSearchText] = useDebouncedValue(searchText, 500);
  const [pages, setPages] = useState<OptionsPage<Value>[]>([]);
  const pagesRef = useRef(pages);
  const [isFirstRequestDone, setIsFirstRequestDone] = useState(false);

  useEffect(() => {
    pagesRef.current = pages;
  }, [pages]);

  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const [errorOptions, setErrorOptions] = useState<Error | null>(null);
  const options: Option<Value>[] = pages.flatMap((page) => page.options);
  const hasMoreOptions = Boolean(pages.at(-1)?.next) ?? false;

  const [mode, setMode] = useState<"select" | "autocomplete">("select");

  /**
   * Each new search text requires a new list for the options.
   */
  useEffect(() => {
    setPages([]);
  }, [searchText]);

  const [isFetchingNextPage, setIsFetchingNextPage] = useState(false);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleOptionsLoad = useCallback(
    async (searchText: string, page: number) => {
      if (searchTextRef.current !== searchText) {
        return null;
      }

      setIsLoadingOptions(true);

      let optionsPage: OptionsPage<Value> | null = null;

      try {
        const pageData = page > 1 ? pagesRef.current.at(page - 2) : undefined;
        const newOptionsPage = await loadOptions(searchText, pageData);
        optionsPage = newOptionsPage;

        if (searchTextRef.current !== searchText || !isMounted.current) {
          return null;
        }

        if (newOptionsPage) {
          setPages((previousPages) => {
            const newPages = [...previousPages];
            newPages[page - 1] = newOptionsPage;

            return newPages;
          });
        }
      } catch (error: unknown) {
        if (isMounted.current) {
          setErrorOptions(error as Error);
        }
      }

      if (isMounted.current) {
        setIsLoadingOptions(false);
        setIsFirstRequestDone(true);
        setIsFetchingNextPage(false);
      }

      return optionsPage;
    },
    [loadOptions]
  );

  const autocompleteContextValue = useMemo(
    () => ({
      isLoadingOptions: isFetchingNextPage,
      hasMoreOptions,
      fetchNextPage: () => {
        if (hasMoreOptions && !isFetchingNextPage) {
          setIsFetchingNextPage(true);
          handleOptionsLoad(searchTextRef.current, pages.length + 1);
        }
      },
    }),
    [isFetchingNextPage, hasMoreOptions, handleOptionsLoad, pages.length]
  );

  const [hasFetchedOnOpen, setHasFetchedOnOpen] = useState(false);

  // Fetches new options when the search text or page changes.
  useEffect(() => {
    if (isOpen) {
      handleOptionsLoad(debouncedSearchText, 1);
    }
  }, [debouncedSearchText, hasFetchedOnOpen, isOpen, handleOptionsLoad]);

  useEffect(() => {
    if (isOpen && !hasFetchedOnOpen) {
      searchTextRef.current = "";
      handleOptionsLoad("", 1).then(() => {
        if (isMounted.current) {
          setHasFetchedOnOpen(true);
        }
      });
    }
  }, [debouncedSearchText, isOpen, handleOptionsLoad, hasFetchedOnOpen]);

  const isLoadedOptions = !isLoadingOptions && isFirstRequestDone;
  const isLoadingFirstPage = isLoadingOptions && pages.length === 0;

  const autocompleteValue = value ??
    allOption ?? {
      label: "",
      value: "" as Value,
    };

  // Materail ui autocomplete component requires always to have the rendered value in the list
  if (!options.some((option) => option.value === autocompleteValue.value)) {
    options.unshift(autocompleteValue);
  }

  if (createOption && !options.some((option) => option === createOption)) {
    options.unshift(createOption);
  }

  // All option must be in the list if provided
  if (allOption && !options.some((option) => option === allOption)) {
    options.unshift(allOption);
  }

  const [hasSetInitialValues, setHasSetInitialValues] = useState(false);

  useEffect(() => {
    if (hasSetInitialValues || !isLoadedOptions) {
      return;
    }

    const firsOption = pages.at(0)?.options.at(0);

    if (autoSelectFirst && firsOption) {
      onChange?.(firsOption);
      setHasSetInitialValues(true);
      return;
    }

    if (defaultOption) {
      onChange?.(defaultOption);
      setHasSetInitialValues(true);
    }
  }, [isLoadedOptions, hasSetInitialValues, pages, autoSelectFirst, defaultOption, onChange]);

  const loadingIndicator = !isLoadedOptions || errorOptions ? <CircularProgress size={25} /> : null;

  // Initial request to pre-load options for first opening of menu.
  useEffect(() => {
    if (!isFirstRequestDone) {
      searchTextRef.current = "";
      setSearchText("");
      handleOptionsLoad("", 1).then((optionsPage) => {
        if (optionsPage && isMounted.current) {
          const hasMorePages = Boolean(optionsPage.next);

          setMode(hasMorePages ? "autocomplete" : "select");
        }
      });
    }
  }, [isFirstRequestDone, handleOptionsLoad]);

  if (showLoadingIndicator && !value && loadingIndicator) {
    return loadingIndicator;
  }

  if (mode === "select") {
    return (
      <FormControl size="small" fullWidth>
        {label && <InputLabel>{label}</InputLabel>}

        <Select
          data-testid="select"
          value={autocompleteValue?.value}
          onChange={(e) => {
            const selectedValue = e.target.value;
            const option = options.find((option) => option.value === selectedValue);
            onChange?.(option);
          }}
          fullWidth
          displayEmpty
          disabled={disabled}
          label={label}
          sx={sx}
        >
          {options.map((option) => (
            <MenuItem
              key={String(option.value)}
              value={option.value}
              sx={{
                fontWeight: option.bold ? "bold" : undefined,
              }}
            >
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  return (
    <AutocompleteContext.Provider value={autocompleteContextValue}>
      <BaseAutocomplete<Option<Value | string> | undefined, false, true>
        data-testid="autocomplete"
        onOpen={() => {
          setIsOpen(true);
          setHasFetchedOnOpen(false);
        }}
        autoHighlight={autoHighlight}
        options={isLoadingFirstPage ? [] : options}
        loading={isLoadingFirstPage}
        size="small"
        disableClearable
        renderInput={(params) => (
          <TextField
            {...params}
            // eslint-disable-next-line react/jsx-no-duplicate-props
            inputProps={{
              ...params.inputProps,
              ...inputProps,
            }}
            label={label}
          />
        )}
        // disable built-in filter to rely on backend filter
        filterOptions={(options) => options}
        onChange={(_, option) => {
          onChange?.(option as Option<Value>);
        }}
        onInputChange={(_event, newValue) => {
          setIsLoadingOptions(true);
          setSearchText(newValue);
        }}
        onFocus={() => {
          // clear to suggest the user to type to filter
          setSearchText("");
        }}
        inputValue={searchText}
        isOptionEqualToValue={(option, value) => option?.value === value?.value}
        value={autocompleteValue}
        renderOption={(props, option) => (
          <ListItem {...props} style={{ fontWeight: option?.bold ? "bold" : undefined }}>
            {option?.label}
          </ListItem>
        )}
        getOptionKey={(option) => `${option?.label}:${option?.value}`}
        getOptionLabel={(option) => option?.label ?? ""}
        popupIcon={popupIcon}
        sx={sx}
        classes={classes}
        disabled={disabled}
        ListboxComponent={Listbox}
      />
    </AutocompleteContext.Provider>
  );
}
